import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import type { Shift } from '@/types'

dayjs.extend(utc)

type DepartmentShift = Pick<Shift, 'department'>
type OwnedShift = Pick<Shift, 'department' | 'employment'>
type PlannedShift = Pick<Shift, 'plan_work_start_at' | 'plan_work_end_at'>
type NamedDepartment = { id: string, name: string }
type ScheduleRow<T> = { id: string, department?: { id: string } | null, is_vacancy?: boolean, shifts?: T[] | null }

export const OTHER_DEPARTMENT_FALLBACK_NAME = 'Другое подразделение'
export const PLAN_TIME_NOT_SET = 'Время не указано'

// Смена другого подразделения: после RD-175 график отдаёт в shifts[] строки и смены
// других трудоустройств сотрудника. Они только для отображения и не участвуют
// в операциях и итогах выбранного подразделения.
export const isOtherDepartmentShift = (shift: DepartmentShift, departmentId?: string | null) => (
    Boolean(departmentId) && shift.department !== departmentId
)

// Смены выбранного подразделения. Без выбранного подразделения список не фильтруется.
export const ownDepartmentShifts = <T extends DepartmentShift>(shifts: T[], departmentId?: string | null) => (
    departmentId ? shifts.filter(shift => shift.department === departmentId) : shifts
)

// RD-182: собственная смена строки графика — её ТУ (shift.employment = row.id) в выбранном подразделении.
// Смены другого ТУ того же подразделения обслуживает его собственная строка; порядок shifts[], is_vacancy
// и признаки дополнительности на принадлежность не влияют. Без выбранного подразделения — только по ТУ.
export const isOwnRowShift = (shift: OwnedShift, row: { id: string }, departmentId?: string | null) => (
    shift.employment === row.id && (!departmentId || shift.department === departmentId)
)

export const ownRowShifts = <T extends OwnedShift>(row: ScheduleRow<T>, departmentId?: string | null) => (
    (row.shifts ?? []).filter(shift => isOwnRowShift(shift, row, departmentId))
)

// Внешняя занятость показывается только в строке собственного ТУ выбранного подразделения;
// строки биржи и других подразделений показывают лишь свои смены этого подразделения.
export const canShowExternalShifts = (row: ScheduleRow<unknown>, departmentId?: string | null) => (
    Boolean(departmentId) && !row.is_vacancy && row.department?.id === departmentId
)

// Внешние смены строки за дату: другое подразделение, повтор shift.id — один раз, по плановому началу.
export const rowExternalShifts = <T extends OwnedShift & PlannedShift & { id?: string, date?: string }>(
    row: ScheduleRow<T>,
    departmentId: string | null | undefined,
    date: string,
) => {
    if (!canShowExternalShifts(row, departmentId)) return []
    const seen = new Set<string>()
    return sortByPlanStart((row.shifts ?? []).filter((shift) => {
        if (!shift.date || !dayjs(shift.date).isSame(date, 'day') || !isOtherDepartmentShift(shift, departmentId)) return false
        if (!shift.id) return true
        if (seen.has(shift.id)) return false
        seen.add(shift.id)
        return true
    }))
}

export const departmentName = (departmentId: string, departments: NamedDepartment[]) => (
    departments.find(item => item.id === departmentId)?.name || OTHER_DEPARTMENT_FALLBACK_NAME
)

// Плановый интервал «ЧЧ:ММ–ЧЧ:ММ» в формате ячейки графика; окончание на следующий день — «+1».
// Границы плана в формате ячейки; нет или невалидно начало/окончание — null (не 00:00).
export const describePlan = (shift: PlannedShift) => {
    if (!shift.plan_work_start_at || !shift.plan_work_end_at) return null

    const start = dayjs.utc(shift.plan_work_start_at)
    const end = dayjs.utc(shift.plan_work_end_at)
    if (!start.isValid() || !end.isValid()) return null

    const dayShift = end.startOf('day').diff(start.startOf('day'), 'day')
    return { start: start.format('HH:mm'), end: end.format('HH:mm'), nextDay: dayShift > 0 ? dayShift : 0 }
}

export const formatPlanInterval = (shift: PlannedShift) => {
    const plan = describePlan(shift)
    if (!plan) return PLAN_TIME_NOT_SET
    return `${plan.start}–${plan.end}${plan.nextDay ? `+${plan.nextDay}` : ''}`
}

export const formatOtherDepartmentShift = (
    shift: DepartmentShift & PlannedShift,
    departments: NamedDepartment[],
) => {
    const name = departments.find(item => item.id === shift.department)?.name || OTHER_DEPARTMENT_FALLBACK_NAME
    return `Смена в ${name} · ${formatPlanInterval(shift)}`
}

// Порядок подсказки не зависит от порядка shifts[] в ответе: по плановому началу, без времени — в конце.
export const sortByPlanStart = <T extends PlannedShift & { id?: string }>(shifts: T[]) => [...shifts].sort((a, b) => {
    const aStart = a.plan_work_start_at ? dayjs.utc(a.plan_work_start_at).valueOf() : Number.POSITIVE_INFINITY
    const bStart = b.plan_work_start_at ? dayjs.utc(b.plan_work_start_at).valueOf() : Number.POSITIVE_INFINITY
    if (aStart !== bStart) return aStart - bStart
    return (a.id ?? '').localeCompare(b.id ?? '')
})

export interface ExternalShiftView {
    key: string
    departmentName: string
    positionName: string
    plan: ReturnType<typeof describePlan>
    planText: string
    isVacancy: boolean
}

// Информационное представление внешней смены: магазин, должность, план, источник «С биржи».
// Факт, часы и нарушения внешней смены не показываются и не рассчитываются.
export const buildExternalShiftView = (
    shift: OwnedShift & PlannedShift & Pick<Shift, 'id' | 'is_vacancy'>,
    departments: NamedDepartment[],
    positionName: string,
): ExternalShiftView => ({
    key: shift.id ?? `${shift.employment}-${shift.department}-${shift.plan_work_start_at}`,
    departmentName: departmentName(shift.department, departments),
    positionName,
    plan: describePlan(shift),
    planText: formatPlanInterval(shift),
    isVacancy: Boolean(shift.is_vacancy),
})
